import { lazy, Suspense } from 'react'
import { Provider } from 'react-redux'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter  } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import store, { persistor } from './store'
import queryClient from 'services/queryClient'
import Theme from 'components/template/Theme'
import Layout from 'components/layout'
import history from './appHistory'
import mockServer from './mock'
import appConfig from 'configs/app.config'
import './locales'

// process.env.NODE_ENV doesn't exist under Vite (vite.config.js deliberately
// defines 'process.env' as an empty object, not a real env dump -- see the
// comment there). import.meta.env.MODE is Vite's equivalent: 'development'
// under `vite`/`npm start`, 'production' under `vite build`.
const environment = import.meta.env.MODE

/**
 * Set enableMock(Default false) to true at configs/app.config.js
 * If you wish to enable mock api
 *
 * This guard was previously commented out, which meant the mock API server
 * (miragejs) initialized unconditionally in every environment, including
 * production builds -- fake seeded data and fake route handlers running
 * alongside, and potentially instead of, real payment/transaction data.
 * Restored as part of increment 3's dependency consolidation; enableMock is
 * also now false by default in configs/app.config.js.
 */
if (environment !== 'production' && appConfig.enableMock) {
	mockServer({ environment })
}

// Dev-only React Query devtools, lazy-loaded so the panel and its deps never
// reach the production bundle -- same discipline as the apexcharts/
// @fullcalendar/quill React.lazy() split from the earlier bundle-size work.
const ReactQueryDevtools =
	environment !== 'production'
		? lazy(() =>
				import('@tanstack/react-query-devtools').then((m) => ({
					default: m.ReactQueryDevtools,
				}))
		  )
		: null

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<Provider store={store}>
				<PersistGate loading={null} persistor={persistor}>
					<BrowserRouter history={history}>
						<Theme>
							<Layout />
						</Theme>
					</BrowserRouter >
				</PersistGate>
			</Provider>
			{ReactQueryDevtools && (
				<Suspense fallback={null}>
					<ReactQueryDevtools initialIsOpen={false} />
				</Suspense>
			)}
		</QueryClientProvider>
	)
}

export default App
